import React from 'react';
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  Tile,
  TileGroup,
  Typography,
  TextField,
  Button,
  SkeletonLine,
  Alert,
} from '@one-thd/sui-atomic-components';
import { IOrderFilterTileOption } from '../../../models/purchase-history/order-filter';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';

const TILE_BATCH_SIZE = 20;

interface IOrderFilterGroupProps {
  title: string;
  tileOptions: IOrderFilterTileOption[];
  selectedOption: string;
  setSelectedOption: (option: string) => void;
}

export const OrderFilterGroup = ({
  title,
  tileOptions,
  selectedOption,
  setSelectedOption,
}: IOrderFilterGroupProps) => {
  const { isPHFilterLoading, cpsFilterError } = usePurchaseHistoryContext();
  const [search, setSearch] = React.useState<string>('');
  const [visibleTileCount, setVisibleTileCount] =
    React.useState(TILE_BATCH_SIZE);

  const filteredTileOptions = React.useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return tileOptions;
    }

    return tileOptions.filter((option) =>
      option.value.toLowerCase().includes(normalizedSearch),
    );
  }, [search, tileOptions]);

  React.useEffect(() => {
    setVisibleTileCount(TILE_BATCH_SIZE);
  }, [search, tileOptions]);

  const visibleTileOptions = filteredTileOptions.slice(0, visibleTileCount);
  const hasMoreTileOptions = filteredTileOptions.length > visibleTileCount;
  const remainingTileOptionsCount = Math.max(
    filteredTileOptions.length - visibleTileCount,
    0,
  );
  const skeletonCount = Math.min(TILE_BATCH_SIZE, 6);

  return (
    <div className="sui-relative sui-pt-[14px]">
      <Accordion noBorders>
        <AccordionHeader aria-controls="accordion1-content">
          {title}
        </AccordionHeader>
        <AccordionBody>
          {cpsFilterError ? (
            <Alert status="error">
              We were unable to retrieve your {title} details. Refresh the page
              and try again.
            </Alert>
          ) : (
            <>
              <div className="sui-mb-[20px]">
                <Typography weight="bold" className="sui-mb-[4px]">
                  Search {title}s
                </Typography>
                <div className="sui-flex sui-items-end sui-gap-[12px] sui-w-full">
                  <TextField
                    value={search}
                    aria-label={`Search ${title}`}
                    placeholder={
                      title === 'PO/Job Name' ? 'PO/Job Name' : `0000`
                    }
                    onChange={(event: { target: { value: string } }) => {
                      const input = event.target.value;
                      setSearch(input);
                    }}
                    fullWidth
                  />
                </div>
              </div>
              {isPHFilterLoading ? (
                <div className="sui-flex sui-flex-col sui-gap-[12px]">
                  {Array.from(
                    { length: skeletonCount },
                    (_value, index) => index,
                  ).map((index) => (
                    <SkeletonLine key={`order-filter-skeleton-${index}`} />
                  ))}
                </div>
              ) : (
                <TileGroup orientation="vertical">
                  <Typography weight="bold" className="sui-mb-[4px]">
                    All {title}s
                  </Typography>
                  {visibleTileOptions.length === 0 && (
                    <Alert status="error" headline="No matches found">
                      Try adjusting your search term or date range filter
                    </Alert>
                  )}
                  {visibleTileOptions.map((option) => (
                    <Tile
                      key={option.value}
                      value={option.value}
                      selected={option.value === selectedOption}
                      onClick={() => {
                        if (option.value === selectedOption) {
                          setSelectedOption(''); // Deselect if the same option is clicked
                        } else {
                          setSelectedOption(option.value);
                        }
                      }}
                    >
                      <Typography className="sui-mr-auto">
                        {option.value}
                      </Typography>
                    </Tile>
                  ))}
                </TileGroup>
              )}
              {!isPHFilterLoading && hasMoreTileOptions && (
                <div className="sui-flex sui-items-center sui-justify-center sui-pt-2">
                  <Button
                    type="button"
                    variant="text"
                    onClick={() =>
                      setVisibleTileCount(
                        (prevCount) => prevCount + TILE_BATCH_SIZE,
                      )
                    }
                  >
                    Show {Math.min(TILE_BATCH_SIZE, remainingTileOptionsCount)}{' '}
                    More
                  </Button>
                </div>
              )}{' '}
            </>
          )}
        </AccordionBody>
      </Accordion>
    </div>
  );
};
